import React, { useState } from 'react';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Download, FileText } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function DeclarationsUrssaf() {
  const { getAuthHeaders } = useAuth();
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchDeclaration = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/declarations/urssaf?month=${month}`, getAuthHeaders());
      setData(response.data);
    } catch (error) {
      toast.error('Erreur lors du chargement de la déclaration');
    } finally {
      setLoading(false);
    }
  };

  const downloadCSV = async () => {
    try {
      const response = await axios.get(
        `${API_URL}/api/declarations/urssaf/export-csv?month=${month}`,
        {
          ...getAuthHeaders(),
          responseType: 'blob',
        }
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `urssaf_${month}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Export CSV téléchargé');
    } catch (error) {
      toast.error('Erreur lors de l\'export CSV');
    }
  };

  const downloadPDF = async () => {
    try {
      const response = await axios.get(
        `${API_URL}/api/declarations/urssaf/export-pdf?month=${month}`,
        {
          ...getAuthHeaders(),
          responseType: 'blob',
        }
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `urssaf_${month}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Export PDF téléchargé');
    } catch (error) {
      toast.error('Erreur lors de l\'export PDF');
    }
  };

  return (
    <div className="space-y-8" data-testid="urssaf-page">
      <div>
        <h1 className="text-4xl font-bold tracking-tight">Déclaration URSSAF</h1>
        <p className="text-muted-foreground mt-2">Calcul mensuel pour votre déclaration</p>
      </div>

      <Card className="rounded-xl border-border/60">
        <CardHeader>
          <CardTitle>Sélectionnez le mois</CardTitle>
          <CardDescription>
            Choisissez le mois pour lequel vous souhaitez générer la déclaration
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex gap-4 items-end">
            <div className="space-y-2 flex-1">
              <Label htmlFor="month">Mois</Label>
              <Input
                id="month"
                type="month"
                value={month}
                onChange={(e) => setMonth(e.target.value)}
                data-testid="input-month"
              />
            </div>
            <Button onClick={fetchDeclaration} disabled={loading} data-testid="btn-calculate">
              Calculer
            </Button>
          </div>
        </CardContent>
      </Card>

      {data && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card className="rounded-xl border-border/60 bg-gradient-to-br from-rose-50 to-orange-50">
              <CardContent className="p-6">
                <p className="text-sm text-muted-foreground mb-2">Total encaissé</p>
                <p className="text-4xl font-bold" data-testid="total-encaisse">{data.total_encaisse.toFixed(2)} €</p>
                {!data.inclure_especes && (
                  <p className="text-xs text-amber-600 mt-2 font-medium">
                    (espèces exclues)
                  </p>
                )}
              </CardContent>
            </Card>
            <Card className="rounded-xl border-border/60 bg-gradient-to-br from-blue-50 to-cyan-50">
              <CardContent className="p-6">
                <p className="text-sm text-muted-foreground mb-2">Ma part ({data.pourcentage.toFixed(0)}%)</p>
                <p className="text-4xl font-bold" data-testid="ma-part">{data.ma_part.toFixed(2)} €</p>
              </CardContent>
            </Card>
          </div>

          <Card className="rounded-xl border-border/60">
            <CardHeader>
              <CardTitle>Exports</CardTitle>
              <CardDescription>
                Téléchargez votre déclaration au format CSV ou PDF
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex gap-4">
                <Button onClick={downloadCSV} variant="outline" data-testid="btn-export-csv">
                  <Download className="w-4 h-4 mr-2" />
                  Télécharger CSV
                </Button>
                <Button onClick={downloadPDF} variant="outline" data-testid="btn-export-pdf">
                  <FileText className="w-4 h-4 mr-2" />
                  Télécharger PDF
                </Button>
              </div>
            </CardContent>
          </Card>

          {data.payments.length > 0 && (
            <Card className="rounded-xl border-border/60">
              <CardHeader>
                <CardTitle>Détail des paiements ({data.payments.length})</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full" data-testid="payments-table">
                    <thead>
                      <tr className="border-b border-border/40">
                        <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Date</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Montant</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Type</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Moyen</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.payments.map((payment, idx) => (
                        <tr key={idx} className="border-b border-border/20">
                          <td className="py-3 px-4 text-sm">{new Date(payment.paid_at).toLocaleDateString('fr-FR')}</td>
                          <td className="py-3 px-4 text-sm font-semibold">{payment.montant.toFixed(2)} €</td>
                          <td className="py-3 px-4 text-sm">{payment.type}</td>
                          <td className="py-3 px-4 text-sm">{payment.moyen}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          )}
        </>
      )}
    </div>
  );
}